import React, { useState, useRef, useEffect } from "react";
import Markdown from "react-markdown";
import { Pencil } from "lucide-react";
import RichTextEditor from "./RichTextEditor"; // Assuming RichTextEditor is in the same directory
import { useTranslation } from "react-i18next";

interface EditableMarkdownProps {
  value: string;
  isEditable: boolean;
  handleSave: (newValue: string) => Promise<void>;
  iconShowDelay?: number; // Delay in ms to show the icon
  iconHideDelay?: number; // Delay in ms to hide the icon after mouse leave
}

const EditableMarkdown: React.FC<EditableMarkdownProps> = ({
  value,
  isEditable,
  handleSave,
  iconShowDelay = 300, // Default 300ms show delay
  iconHideDelay = 300, // Default 300ms hide delay
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedValue, setEditedValue] = useState(value);
  const [showEditIcon, setShowEditIcon] = useState(false);
  const [isHoveringIcon, setIsHoveringIcon] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update editedValue if the external value prop changes while not editing
  useEffect(() => {
    if (!isEditing) {
      setEditedValue(value);
    }
  }, [value, isEditing]);

  const clearTimeouts = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    if (!isEditable || isEditing) return;
    clearTimeouts(); // Clear any pending hide timeout
    hoverTimeoutRef.current = setTimeout(() => {
      setShowEditIcon(true);
    }, iconShowDelay);
  };

  const handleMouseLeave = () => {
    if (!isEditable || isEditing) return;
    clearTimeouts(); // Clear any pending show timeout
    hideTimeoutRef.current = setTimeout(() => {
      // Only hide if not hovering the icon itself
      if (!isHoveringIcon) {
        setShowEditIcon(false);
      }
    }, iconHideDelay);
  };

  const handleIconMouseEnter = () => {
    if (!isEditable || isEditing) return;
    setIsHoveringIcon(true);
    clearTimeouts(); // Keep icon visible while hovering it
    setShowEditIcon(true); // Ensure it's visible
  };

  const handleIconMouseLeave = () => {
    if (!isEditable || isEditing) return;
    setIsHoveringIcon(false);
    // Start hide timeout again when leaving the icon
    handleMouseLeave();
  };

  const handleEditClick = () => {
    if (!isEditable) return;
    setIsEditing(true);
    setIsSaving(false);
    setShowEditIcon(false); // Hide icon immediately when editing starts
    clearTimeouts(); // Ensure no timeouts are running
  };

  const handleSaveClick = async () => {
    setIsSaving(true);
    await handleSave(editedValue);
    setIsSaving(false);
    setIsEditing(false);
  };

  const handleCancelClick = () => {
    setEditedValue(value); // Revert changes
    setIsEditing(false);
    setIsSaving(false);
  };

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, []);

  return (
    <div
      className="relative group max-w-none" // Added prose for markdown styling
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isEditable && !isEditing && (
        <button
          onClick={handleEditClick}
          onMouseEnter={handleIconMouseEnter}
          onMouseLeave={handleIconMouseLeave}
          className={`btn btn-ghost btn-sm absolute -top-8 right-0 px-4 rounded-full text-base-content transition-all duration-300 ease-in-out z-10 flex items-center ${
            showEditIcon
              ? isHoveringIcon
                ? "opacity-100 scale-105 bg-base-300"
                : "opacity-75 scale-100"
              : "opacity-0 pointer-events-none"
          } hover:opacity-100 hover:scale-105`}
          aria-label="Edit"
        >
          <Pencil size={14} className="mr-0.5" />
          <span
            className={`ml-1 overflow-hidden transition-all duration-500 ease-in-out ${
              isHoveringIcon ? "max-w-xs" : "max-w-0"
            }`}
          >
            {t("common.edit")}
          </span>
        </button>
      )}

      {isEditing ? (
        <div className="mt-1">
          <RichTextEditor
            value={editedValue}
            onChange={setEditedValue}
            onSave={handleSaveClick}
            onCancel={handleCancelClick}
            isSaving={isSaving}
          />
        </div>
      ) : (
        // Use div wrapper for consistent padding/margin if needed, or apply directly
        // Add min-h-[2rem] to prevent layout shift when empty
        <div className="min-h-[2rem] py-1 prose dark:prose-invert">
          <Markdown>{value || "*No content*"}</Markdown>
        </div>
      )}
    </div>
  );
};

export default EditableMarkdown;
